import React from 'react';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

function TemplatePreview({ mode, template, onBack, onUseTemplate }) {
  const rawSchema = Array.isArray(template?.schema_json) ? template.schema_json : [];
  const isSectionSchema = Array.isArray(rawSchema[0]?.fields);
  const sections = isSectionSchema
    ? rawSchema
    : rawSchema.length
      ? [
          {
            id: 'section-1',
            title: 'Section 1',
            description: '',
            fields: rawSchema,
          },
        ]
      : [];

  const renderPreviewField = (field) => {
    const commonStyle = {
      width: '100%',
      border: `1px solid ${mode === 'dark' ? '#555' : '#d5d7da'}`,
      borderRadius: 8,
      background: mode === 'dark' ? '#191919' : '#fff',
      color: mode === 'dark' ? '#fff' : '#222',
      padding: '10px 12px',
      fontSize: 14,
      boxSizing: 'border-box',
    };

    if (field.type === 'attachment') {
      return (
        <div
          style={{
            ...commonStyle,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            cursor: 'not-allowed',
            opacity: 0.95,
          }}
        >
          <span style={{ fontSize: 13, color: mode === 'dark' ? '#cbd5e1' : '#475569' }}>
            {field.placeholder || 'Choose file (PDF, Word, or image)'}
          </span>
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: '#009391',
              whiteSpace: 'nowrap',
            }}
          >
            Browse…
          </span>
        </div>
      );
    }

    if (field.type === 'multi_select') {
      const opts = field.options || [];
      const borderColor = mode === 'dark' ? '#4a4a4a' : '#d0d5dd';
      const rowBorder = mode === 'dark' ? '#2a2a2a' : '#e5e7eb';
      return (
        <div
          style={{
            width: '100%',
            borderRadius: 8,
            overflow: 'hidden',
            border: `1px solid ${borderColor}`,
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              padding: '8px 10px',
              fontSize: 12,
              fontWeight: 600,
              color: mode === 'dark' ? '#94a3b8' : '#64748b',
              background: mode === 'dark' ? '#141414' : '#f1f5f9',
              borderBottom: `1px solid ${borderColor}`,
            }}
          >
            Select one or more options
          </div>
          <div
            style={{
              background: mode === 'dark' ? '#191919' : '#fff',
              maxHeight: 200,
              overflowY: 'auto',
            }}
          >
            {opts.length === 0 ? (
              <div
                style={{
                  padding: '10px 12px',
                  fontSize: 13,
                  color: mode === 'dark' ? '#94a3b8' : '#64748b',
                }}
              >
                No options configured
              </div>
            ) : (
              opts.map((option, index) => (
                <label
                  key={`${field.id || field.label}-ms-${index}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '9px 10px',
                    borderBottom:
                      index < opts.length - 1 ? `1px solid ${rowBorder}` : 'none',
                    cursor: 'not-allowed',
                    fontSize: 13,
                    color: mode === 'dark' ? '#e4e7ec' : '#344054',
                    margin: 0,
                  }}
                >
                  <input type="checkbox" disabled />
                  <span>{option}</span>
                </label>
              ))
            )}
          </div>
        </div>
      );
    }

    if (field.type === 'dropdown') {
      return (
        <select style={commonStyle} disabled>
          <option>{field.placeholder || `Select ${field.label || 'option'}`}</option>
          {(field.options || []).map((option, index) => (
            <option key={`${field.id || field.label}-opt-${index}`}>{option}</option>
          ))}
        </select>
      );
    }

    if (field.type === 'textarea') {
      return (
        <textarea
          rows={3}
          style={{ ...commonStyle, resize: 'vertical' }}
          placeholder={field.placeholder || ''}
          disabled
        />
      );
    }

    if (field.type === 'radio' || field.type === 'checkbox') {
      return (
        <div style={{ display: 'grid', gap: 6 }}>
          {(field.options || []).map((option, index) => (
            <label
              key={`${field.id || field.label}-opt-${index}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                color: mode === 'dark' ? '#e4e4e4' : '#344054',
                fontSize: 13,
              }}
            >
              <input type={field.type} disabled />
              {option}
            </label>
          ))}
          {(!field.options || field.options.length === 0) && (
            <div style={{ color: mode === 'dark' ? '#98a2b3' : '#667085', fontSize: 12 }}>
              No options configured
            </div>
          )}
        </div>
      );
    }

    const inputType = field.type === 'number' ? 'number' : field.type === 'email' ? 'email' : 'text';
    return <input type={inputType} style={commonStyle} placeholder={field.placeholder || ''} disabled />;
  };

  return (
    <section
      style={{
        padding: '16px 18px',
      }}
    >
      <div style={{ marginBottom: 14, display: 'flex', justifyContent: 'space-between', gap: 10 }}>
        <div>
          <button
            type="button"
            onClick={onBack}
            style={{
              border: `1px solid ${mode === 'dark' ? '#4a4a4a' : '#d0d5dd'}`,
              background: 'transparent',
              color: mode === 'dark' ? '#fff' : '#111',
              borderRadius: 8,
              padding: '7px 12px',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              marginBottom: 18,
            }}
          >
            <ArrowBackIcon style={{ fontSize: 18 }} />
            Back to library
          </button>
          <h2
            style={{
              margin: 0,
              fontSize: 20,
              fontWeight: 600,
              color: mode === 'dark' ? '#fff' : '#222',
            }}
          >
            {template?.title || 'Template Preview'}
          </h2>
          <p
            style={{
              marginTop: 6,
              marginBottom: 0,
              color: mode === 'dark' ? '#bdbdbd' : '#667085',
              fontSize: 14,
            }}
          >
            {template?.description || 'Template form preview'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => onUseTemplate(template)}
          style={{
            border: 'none',
            borderRadius: 8,
            background: '#009391',
            color: '#fff',
            fontWeight: 600,
            fontSize: 13,
            padding: '10px 14px',
            cursor: 'pointer',
            height: 40,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <AddIcon style={{ fontSize: 18 }} />
          Use template
        </button>
      </div>

      <div
        style={{
          padding: 14,
        }}
      >
        {sections.length === 0 && (
          <div style={{ color: mode === 'dark' ? '#ccc' : '#666', fontSize: 14 }}>
            No fields available in this template.
          </div>
        )}

        {sections.map((section, sectionIndex) => (
          <div
            key={section.id || `section-${sectionIndex + 1}`}
            style={{
              border: `1px solid ${mode === 'dark' ? '#343434' : '#e5e7eb'}`,
              borderRadius: 10,
              padding: 12,
              marginBottom: 12,
              background: mode === 'dark' ? '#141414' : '#fff',
            }}
          >
            <div
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: mode === 'dark' ? '#f2f4f7' : '#1d2939',
                marginBottom: 4,
              }}
            >
              {section.title || `Section ${sectionIndex + 1}`}
            </div>
            {section.description ? (
              <div
                style={{
                  fontSize: 13,
                  color: mode === 'dark' ? '#98a2b3' : '#667085',
                  marginBottom: 10,
                }}
              >
                {section.description}
              </div>
            ) : null}

            {(section.fields || []).map((field, fieldIndex) => (
              <div key={field.id || `${field.label}-${fieldIndex}`} style={{ marginBottom: 12 }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: 6,
                    fontSize: 13,
                    fontWeight: 500,
                    color: mode === 'dark' ? '#e4e4e4' : '#344054',
                  }}
                >
                  {field.label || `Field ${fieldIndex + 1}`} {field.required ? '*' : ''}
                </label>
                {renderPreviewField(field)}
              </div>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

export default TemplatePreview;
